// LazyAnimations.js - JavaScript version of lazy loading animations with stagger effects
export class LazyAnimations {
  constructor(options = {}) {
    this.options = {
      threshold: 0.1,
      rootMargin: '50px',
      staggerDelay: 100, // milliseconds between each item animation
      animationDuration: 600,
      ...options
    };
    
    this.observer = null;
    this.animatedElements = new Set();
    this.init();
  }

  init() {
    if (!('IntersectionObserver' in window)) {
      // Fallback for older browsers - show all elements
      this.fallbackAnimation();
      return;
    }

    this.createObserver();
    this.observeElements();
  }

  createObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting && !this.animatedElements.has(entry.target)) {
          this.animateElement(entry.target, index);
          this.animatedElements.add(entry.target);
          this.observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: this.options.threshold,
      rootMargin: this.options.rootMargin
    });
  }

  observeElements() {
    // Observe different types of elements
    const selectors = [
      '.gh-card', // Ghost post cards
      '.gh-postfeed .gh-card-link', // Post feed items
      '.carousel-slide', // Carousel slides
      '.lazy-animate', // Custom lazy animation class
      'article', // Article elements
      '.gh-feature-image', // Feature images
      '.gh-content > *', // Content paragraphs and elements
    ];

    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach((element, index) => {
        if (!this.animatedElements.has(element)) {
          // Add initial hidden state
          this.setInitialState(element);
          this.observer.observe(element);
        }
      });
    });
  }

  setInitialState(element) {
    element.style.opacity = '0';
    element.style.transform = 'translateY(30px)';
    element.style.transition = `opacity ${this.options.animationDuration}ms ease-out, transform ${this.options.animationDuration}ms ease-out`;
  }

  animateElement(element, baseIndex = 0) {
    // Calculate stagger delay based on element position and type
    const elementRect = element.getBoundingClientRect();
    const siblings = Array.from(element.parentNode?.children || []).filter(el => 
      el.tagName === element.tagName && el !== element
    );
    const elementIndex = siblings.indexOf(element);
    const delay = Math.max(0, elementIndex * this.options.staggerDelay);

    setTimeout(() => {
      element.style.opacity = '1';
      element.style.transform = 'translateY(0)';
      
      // Add hover effects after animation
      this.addHoverEffects(element);
      
      // Add success class for CSS targeting
      element.classList.add('lazy-animated');
      
      // Trigger custom event
      element.dispatchEvent(new CustomEvent('lazyAnimated', { 
        detail: { element, delay } 
      }));
      
    }, delay);
  }

  addHoverEffects(element) {
    // Add hover effects for different element types
    if (element.classList.contains('gh-card') || element.querySelector('.gh-card-link')) {
      this.addCardHoverEffect(element);
    }
    
    if (element.tagName === 'IMG' || element.classList.contains('gh-feature-image')) {
      this.addImageHoverEffect(element);
    }
  }

  addCardHoverEffect(element) {
    const card = element.classList.contains('gh-card') ? element : element.querySelector('.gh-card-link');
    if (!card) return;

    let hoverTimeout;
    
    card.addEventListener('mouseenter', () => {
      clearTimeout(hoverTimeout);
      card.style.transform = 'translateY(-8px) scale(1.02)';
      card.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
      card.style.transition = 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    });

    card.addEventListener('mouseleave', () => {
      card.style.transform = 'translateY(0) scale(1)';
      card.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
      
      // Reset to original transition after hover
      hoverTimeout = setTimeout(() => {
        card.style.transition = `opacity ${this.options.animationDuration}ms ease-out, transform ${this.options.animationDuration}ms ease-out`;
      }, 300);
    });
  }

  addImageHoverEffect(element) {
    const img = element.tagName === 'IMG' ? element : element.querySelector('img');
    if (!img) return;

    img.addEventListener('mouseenter', () => {
      img.style.transform = 'scale(1.05)';
      img.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    });

    img.addEventListener('mouseleave', () => {
      img.style.transform = 'scale(1)';
    });
  }

  // Animate specific elements manually
  animateElementsIn(selector, options = {}) {
    const elements = document.querySelectorAll(selector);
    const delay = options.staggerDelay || this.options.staggerDelay;
    
    elements.forEach((element, index) => {
      if (!this.animatedElements.has(element)) {
        this.setInitialState(element);
        setTimeout(() => {
          this.animateElement(element, index);
        }, index * delay);
      }
    });
  }

  // Loading animation for infinite scroll
  showLoadingAnimation(container) {
    const loadingHTML = `
      <div class="potred-loading-dots" style="
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        padding: 2rem;
      ">
        ${[0, 1, 2].map(index => `
          <div class="loading-dot" style="
            width: 12px;
            height: 12px;
            background: linear-gradient(45deg, #ea580c, #dc2626);
            border-radius: 50%;
            animation: loadingBounce 0.8s ease-in-out infinite alternate;
            animation-delay: ${index * 0.2}s;
          "></div>
        `).join('')}
      </div>
    `;
    
    if (typeof container === 'string') {
      container = document.querySelector(container);
    }
    
    if (container) {
      container.innerHTML = loadingHTML;
    }
  }

  // Section end animation
  showSectionEndAnimation(container, options = {}) {
    const endHTML = `
      <div class="potred-section-end" style="
        padding: 3rem 0;
        text-align: center;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.6s ease-out;
      ">
        <div class="section-end-line" style="
          width: 100%;
          max-width: 32rem;
          height: 1px;
          background: linear-gradient(90deg, transparent, #ea580c80, transparent);
          margin: 0 auto 1.5rem;
          transform: scaleX(0);
          transition: transform 1.2s cubic-bezier(0.4, 0, 0.2, 1);
        "></div>
        
        <div class="section-end-ornament" style="
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          margin-bottom: 1rem;
          opacity: 0;
          transform: scale(0);
          transition: all 0.6s ease-out 0.8s;
        ">
          ${[0, 1, 2].map(index => `
            <div style="
              width: 4px;
              height: 4px;
              background: linear-gradient(45deg, #ea580c, #dc2626);
              border-radius: 50%;
              transform: scale(0);
              animation: ornamentPop 0.4s ease-out ${1 + index * 0.1}s forwards;
            "></div>
          `).join('')}
        </div>
        
        <div class="section-end-message" style="
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(234, 88, 12, 0.2);
          border-radius: 12px;
          padding: 1rem 1.5rem;
          display: inline-block;
          opacity: 0;
          transform: translateY(10px);
          transition: all 0.5s ease-out 1.3s;
        ">
          <p style="
            background: linear-gradient(45deg, #ea580c, #dc2626);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            font-size: 1.125rem;
            font-weight: 600;
            margin: 0 0 0.25rem 0;
          ">
            ${options.title || 'That\'s all the posts for now'}
          </p>
          <p style="
            color: #ea580c80;
            font-size: 0.875rem;
            margin: 0;
          ">
            ${options.subtitle || 'Check back soon for more content'}
          </p>
        </div>
      </div>
    `;
    
    if (typeof container === 'string') {
      container = document.querySelector(container);
    }
    
    if (container) {
      container.innerHTML = endHTML;
      
      // Trigger animations
      setTimeout(() => {
        const sectionEnd = container.querySelector('.potred-section-end');
        const line = container.querySelector('.section-end-line');
        const ornament = container.querySelector('.section-end-ornament');
        const message = container.querySelector('.section-end-message');
        
        if (sectionEnd) {
          sectionEnd.style.opacity = '1';
          sectionEnd.style.transform = 'translateY(0)';
        }
        
        if (line) {
          line.style.transform = 'scaleX(1)';
        }
        
        setTimeout(() => {
          if (ornament) {
            ornament.style.opacity = '1';
            ornament.style.transform = 'scale(1)';
          }
        }, 800);
        
        setTimeout(() => {
          if (message) {
            message.style.opacity = '1';
            message.style.transform = 'translateY(0)';
          }
        }, 1300);
      }, 100);
    }
  }

  fallbackAnimation() {
    // Simple fallback for browsers without Intersection Observer
    const elements = document.querySelectorAll('.gh-card, article, .lazy-animate');
    elements.forEach((element, index) => {
      setTimeout(() => {
        element.style.opacity = '1';
        element.style.transform = 'translateY(0)';
        element.classList.add('lazy-animated');
      }, index * 50);
    });
  }

  // Refresh observer for dynamically added content
  refresh() {
    this.observeElements();
  }

  // Clean up
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.animatedElements.clear();
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.PotredLazyAnimations = new LazyAnimations();
  });
} else {
  window.PotredLazyAnimations = new LazyAnimations();
}

export default LazyAnimations;